<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronUp,
    faChevronDown,
    faRotate,
    faCalendarDay,
    faArrowUp,
    faArrowDown,
  } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '../lib/Footer.svelte';
  import { page } from '$stores/pageStore';
  import DailyComplianceChart from './dailyCompliance/DailyComplianceChart.svelte';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let agps = [];
  let chartAgps = [];
  let compliancesCount = 0;
  let totalPages = 1;
  let dayRange = 14; // days
  import { studyProperties } from '$stores/configStore';
  // Use participant's study CGM interval to derive bucket size (hours view still 60 by default)
  $: studyInterval = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;
  let bucketSize = 60; // minutes (used for AGP grouping)
  let isLoading = true;
  let chartVisible = false;
  let selectedType = 'CGM';
  let isModalOpen = false;
  let selectedRawData = '';
  let selectedDate = '';
  let dailyReadings = {};
  let allDates = []; // New array to store all dates in range
  let showReverse = true;
  let inRange = true;
  // Add this helper function to generate date range
  function generateDateRange(endDate, days) {
    const dates = [];
    const end = new Date(endDate + 'T00:00:00'); // Ensure midnight on the end date

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(end);
      date.setDate(end.getDate() - i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  }

  async function fetchAGP(days = 7) {
    try {
      isLoading = true;
      // Generate all dates in range
      allDates = generateDateRange(selectedDate, days);

      let url = `${API_BASE_URL}/glucoseReadings/agp?subjectGUID=${$selectedParticipant._id}&type=${selectedType}&inRange=${inRange}`;

      if (selectedDate) {
        url += `&day=${selectedDate}&days=${days}&timezone=${$selectedParticipant.timezoneCode}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch cgm');
      }

      const data = await res.json();
      const readings = data.data || [];
      compliancesCount = readings.length || 0;
      agps = readings;

      // Group readings into n-minute buckets for full 24 hours
      const timeGroupedReadings = {};
      //const BUCKET_SIZE = 60; // minutes
      const TOTAL_MINUTES = 24 * 60; // 1440 minutes in a day

      // Initialize all buckets for 24 hours
      for (let i = 0; i < TOTAL_MINUTES; i += bucketSize) {
        timeGroupedReadings[i] = [];
      }

      readings.forEach((reading) => {
        // Convert reading time to participant's local timezone
        const readDate = new Date(reading.read).toLocaleString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
        });
        const localDate = new Date(readDate);

        // Calculate minutes based on local time
        const minutes = localDate.getHours() * 60 + localDate.getMinutes();
        const bucketKey = Math.floor(minutes / bucketSize) * bucketSize;

        if (bucketKey >= 0 && bucketKey < TOTAL_MINUTES) {
          timeGroupedReadings[bucketKey].push(reading.value);
        }
      });

      // Calculate percentiles for each time bucket
      let agpData = Object.entries(timeGroupedReadings).map(([minute, values]) => {
        values.sort((a, b) => a - b);
        return {
          minute: parseInt(minute),
          p5: percentile(values, 5),
          p25: percentile(values, 25),
          p50: percentile(values, 50),
          p75: percentile(values, 75),
          p95: percentile(values, 95),
        };
      });

      // Sort by minute of day
      agpData.sort((a, b) => a.minute - b.minute);

      // Append first hour's data to the end (for smooth transition in chart)
      const firstHourData = agpData
        .filter((d) => d.minute < 60)
        .map((d) => ({
          ...d,
          minute: d.minute + TOTAL_MINUTES,
        }));

      agpData = [...agpData, ...firstHourData];

      chartAgps = agpData;
      isLoading = false;

      // Initialize dailyReadings with empty arrays for all dates
      dailyReadings = Object.fromEntries(allDates.map((date) => [date, []]));

      // Then populate with actual readings
      readings.forEach((reading) => {
        // Convert reading time to participant's local timezone
        const readDate = new Date(reading.read).toLocaleString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
        });
        const localDate = new Date(readDate);

        // Create a date key that represents midnight-to-midnight in participant's timezone
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;

        if (!dailyReadings[dateKey]) {
          dailyReadings[dateKey] = [];
        }

        // Create a new reading object with the local time
        const localReading = {
          ...reading,
          read: localDate.toISOString(), // Convert local date back to ISO string
        };

        dailyReadings[dateKey].push(localReading);
      });

      // Sort readings within each day
      Object.keys(dailyReadings).forEach((date) => {
        dailyReadings[date].sort((a, b) => new Date(a.read) - new Date(b.read));
      });

      //console.info(dailyReadings);
    } catch (err) {
      isLoading = false;
      //console.error('Error fetching agps:', err);
      chartAgps = [];
      agps = [];
    }
  }

  // Helper function to calculate percentiles
  function percentile(arr, p) {
    if (arr.length === 0) return null;
    if (arr.length === 1) return arr[0];

    const index = (p / 100) * (arr.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (upper === lower) return arr[index];

    return arr[lower] * (1 - weight) + arr[upper] * weight;
  }

  async function fetchLastCgmDay() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=1&sortOrder=desc&subjectGUID=${$selectedParticipant._id}&timezone=${$selectedParticipant.timezoneCode}&type=CGM&inRange=false`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch last CGM day');
      }
      const data = await res.json();
      const first = data?.data?.[0];
      return first?.day || '';
    } catch (err) {
      console.error('Error fetching last CGM day:', err);
      return '';
    }
  }

  onMount(async () => {
    const urlDate = $page.url.searchParams.get('date');
    if (urlDate) {
      selectedDate = urlDate;
    } else {
      const lastCgmDay = await fetchLastCgmDay();
      if (lastCgmDay) {
        selectedDate = lastCgmDay;
      } else {
        const localDate = new Date().toLocaleDateString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const [month, day, year] = localDate.split('/');
        selectedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      const url = new URL(window.location);
      url.searchParams.set('date', selectedDate);
      goto(url.toString(), { replaceState: true });
    }
    fetchAGP(dayRange);
  });

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      //fetchCGMs(newPage);
    }
  }

  function handleRefresh() {
    fetchAGP(dayRange);
  }

  function showModal(rawData) {
    selectedRawData = rawData;
    isModalOpen = true;
  }

  function closeModal() {
    isModalOpen = false;
    selectedRawData = '';
  }

  function toggleOrder() {
    showReverse = !showReverse;
  }

  function handleDateChange() {
    const url = new URL(window.location);
    if (selectedDate) {
      url.searchParams.set('date', selectedDate);
    } else {
      url.searchParams.delete('date');
    }
    goto(url.toString(), { replaceState: true });
    fetchAGP(dayRange);
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Wear Compliance" 
  description="This page shows daily glucose readings patterns for the selected participant. Each row represents a day with colored bars showing when readings were collected. The dark color represents backfilled readings. You can adjust the starting date and the range."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<Toolbar title="Wear Compliance" itemCountTitle="Items" itemCount={compliancesCount}>
  <div class="text-m flex items-center gap-2">
    End Date
    <input
      type="date"
      bind:value={selectedDate}
      on:input={handleDateChange}
      class="h-6 w-28 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
    />
    <button
      on:click={() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        selectedDate = `${year}-${month}-${day}`;
        dayRange = 7;
        bucketSize = 60;
        handleDateChange();
      }}
      title="Set to today"
      class="h-6 pagination mx-1 me-5 p-0"
    >
      <FontAwesomeIcon icon={faCalendarDay} />
    </button>
    Range
    <select
      bind:value={dayRange}
      on:change={() => fetchAGP(dayRange)}
      class="form-select h-6 w-28 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
    >
      <option value={1} selected={dayRange === 1}>1 Day</option>
      <option value={7} selected={dayRange === 7}>1 Week</option>
      <option value={14} selected={dayRange === 14}>2 Weeks</option>
      <option value={28} selected={dayRange === 28}>4 Weeks</option>
      <option value={42} selected={dayRange === 42}>6 Weeks</option>
      <option value={56} selected={dayRange === 56}>8 Weeks</option>
      <option value={70} selected={dayRange === 70}>10 Weeks</option>
      <option value={84} selected={dayRange === 84}>12 Weeks</option>
    </select>

    <button class="pagination mx-1 p-0" on:click={toggleOrder} title="Toggle order">
      {#if showReverse}
        <FontAwesomeIcon icon={faArrowUp} />
      {:else}
        <FontAwesomeIcon icon={faArrowDown} />
      {/if}
    </button>

    <div class="flex items-center mx-2 print:hidden">
      <input
        type="checkbox"
        bind:checked={inRange}
        on:change={() => handleRefresh()}
        class="w-4 h-6 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
      />
      <span class="ms-2">Valid Device Range</span>
    </div>

    <button on:click={() => handleRefresh()} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="mt-4">
  <!-- Time header -->
  <div class="flex px-12 mb-2">
    <div class="w-8"></div>
    <!-- Space for date labels -->
    <div class="flex-1 flex justify-between px-2">
      {#each Array(23) as _, i}
        <span class="text-xs text-gray-500 dark:text-gray-200"
          >{(i + 1).toString().padStart(2, '0')}</span
        >
      {/each}
    </div>
  </div>

  {#if isLoading}
    <div
      class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1"
    >
      <div class="loader mb-4"></div>
      <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
        preparing compliance report
      </p>
    </div>
  {:else if Object.keys(dailyReadings).length > 0}
    <div class="flex flex-wrap justify-start px-0 pb-1">
      {#each showReverse ? [...allDates].reverse() : allDates as date}
        <DailyComplianceChart readings={dailyReadings[date] || []} {date} />
      {/each}
    </div>
  {/if}

  <!-- Legend  -->
  <Footer>
    <div class="flex items-center gap-2">
      <div class="flex items-center gap-2">
        <div class="w-4 h-2 bg-[rgba(60,170,170,0.75)]"></div>
        <span class="text-xs text-gray-600 dark:text-gray-300">Regular</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-4 h-2 bg-[rgba(50,130,170,0.75)]"></div>
        <span class="text-xs text-gray-600 dark:text-gray-300">Backfilled</span>
      </div>
    </div>
  </Footer>
</div>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
